import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Paper,
  IconButton,
  Switch,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Divider,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Grid,
  Chip,
  useTheme
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { TableField, TableSelectionProps, Table } from "../../types/interfaces";
import API from "../../api/axiosInstance";

const TableSelection: React.FC<TableSelectionProps> = ({ reportData, setReportData, onNext }) => {
  const theme = useTheme();
  const [layoutConfig, setLayoutConfig] = useState<any[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [allTables, setAllTables] = useState<Table[]>([]);
  const [selectedTableID, setSelectedTableID] = useState("");
  const [customTitle, setCustomTitle] = useState("");
  const [availableFields, setAvailableFields] = useState<TableField[]>([]);
  const [selectedFields, setSelectedFields] = useState<string[]>([]);
  const [totalsMap, setTotalsMap] = useState<Record<string, string[]>>({});
  const [activeTableIndex, setActiveTableIndex] = useState<number | null>(null);

  useEffect(() => {
    try {
      const parsed = JSON.parse(reportData.layout || "[]");
      setLayoutConfig(Array.isArray(parsed) ? parsed : []);
    } catch {
      setLayoutConfig([]);
    }
  }, [reportData.layout]);

  useEffect(() => {
    const fetchTables = async () => {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const headers = {
        headers: {
          authToken: user.user?.authtoken,
          sessionId: user.user?.sessionId,
        },
      };
      const response = await API.get("/tables?visibility=published", headers);
      setAllTables(response.data);
    };
    fetchTables();
  }, []);

  const handleTableSelect = async (tableID: string) => {
    setSelectedTableID(tableID);

    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const headers = {
      headers: {
        authToken: user.user?.authtoken,
        sessionId: user.user?.sessionId,
      },
    };
    const res = await API.get(`/tables/${tableID}`, headers);
    setAvailableFields(res.data.fields || []);
  };

  const handleDialogSave = () => {
    const tableInfo = allTables.find(t => t.tableID === selectedTableID);
    const newConfig = {
      id: activeTableIndex != null ? layoutConfig[activeTableIndex].id :"id-" + Math.random().toString(36).substr(2, 9) + "-" + Date.now(),
      tableID: selectedTableID,
      tableName: tableInfo?.tableName || "",
      title: customTitle,
      fields: selectedFields,
      totalsMap,
    };

    const updated = [...layoutConfig];
    if (activeTableIndex != null) {
      updated[activeTableIndex] = newConfig;
    } else {
      updated.push(newConfig);
    }

    setLayoutConfig(updated);
    setReportData({ ...reportData, layout: JSON.stringify(updated) });
    resetDialog();
  };

  const resetDialog = () => {
    setDialogOpen(false);
    setSelectedTableID("");
    setSelectedFields([]);
    setTotalsMap({});
    setAvailableFields([]);
    setCustomTitle("");
    setActiveTableIndex(null);
  };

  const moveTable = (index: number, direction: number) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= layoutConfig.length) return;
    const updated = [...layoutConfig];
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    setLayoutConfig(updated);
    setReportData({ ...reportData, layout: JSON.stringify(updated) });
  };

  const moveField = (index: number, direction: number) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= selectedFields.length) return;
    const updated = [...selectedFields];
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    setSelectedFields(updated);
  };

  const toggleTotal = (fieldID: string, type: string) => {
    setTotalsMap(prev => {
      const current = prev[fieldID] || [];
      const newSet = current.includes(type)
        ? current.filter(t => t !== type)
        : [...current, type];
      return { ...prev, [fieldID]: newSet };
    });
  };

  const handleEdit = async (index: number) => {
    const item = layoutConfig[index];
    setActiveTableIndex(index);
    setSelectedTableID(item.tableID);
    setCustomTitle(item.title || "");
    setSelectedFields(item.fields || []);
    setTotalsMap(item.totalsMap || {});
    await handleTableSelect(item.tableID);
    setDialogOpen(true);
  };

  const handleDelete = (index: number) => {
    const updated = [...layoutConfig];
    updated.splice(index, 1);
    setLayoutConfig(updated);
    setReportData({ ...reportData, layout: JSON.stringify(updated) });
  };

  const summaryOptions = ["Count", "Min", "Max", "Sum", "Average", "Range"];

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Step 2: Select Tables & Fields
      </Typography>

      {layoutConfig.map((item, idx) => (
        <Paper key={item.id} sx={{ p: 2, mb: 2 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="subtitle1">{item.title}</Typography>
              <Typography variant="body2">From: {item.tableName}</Typography>
            </Box>
            <Box>
              <IconButton onClick={() => moveTable(idx, -1)} disabled={idx === 0}>
                <ArrowUpwardIcon />
              </IconButton>
              <IconButton onClick={() => moveTable(idx, 1)} disabled={idx === layoutConfig.length - 1}>
                <ArrowDownwardIcon />
              </IconButton>
              <IconButton onClick={() => handleEdit(idx)}><EditIcon /></IconButton>
              <IconButton onClick={() => handleDelete(idx)}><DeleteIcon color="error" /></IconButton>
            </Box>
          </Box>
        </Paper>
      ))}

      <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>Add Table</Button>
      <Box mt={3}>
        <Button variant="contained" onClick={onNext}>Next</Button>
      </Box>

      <Dialog open={dialogOpen} onClose={resetDialog} maxWidth="md" fullWidth>
        <DialogTitle>{activeTableIndex != null ? "Edit Table" : "Add Table"}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Select Table</InputLabel>
            <Select
              value={selectedTableID}
              onChange={(e) => handleTableSelect(e.target.value)}
              fullWidth
              label="Select Table"
              disabled={!!(activeTableIndex != null)}
            >
              {allTables.map(tbl => (
                <MenuItem key={tbl.tableID} value={tbl.tableID}>{tbl.tableName}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Custom Table Title"
            value={customTitle}
            onChange={(e) => setCustomTitle(e.target.value)}
            sx={{ mb: 3 }}
          />

          {selectedTableID && (
            <>
              <Typography variant="subtitle2" gutterBottom>Select Fields</Typography>
              {selectedFields.map((fieldID, idx) => {
                const field = availableFields.find(f => f.fieldID === fieldID);
                return (
                  <Box key={fieldID} display="flex" alignItems="center" mb={1}>
                    <Typography flex={1}>{field?.fieldName || fieldID}</Typography>
                    <IconButton size="small" onClick={() => moveField(idx, -1)} disabled={idx === 0}>
                      <ArrowUpwardIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => moveField(idx, 1)} disabled={idx === selectedFields.length - 1}>
                      <ArrowDownwardIcon fontSize="small" />
                    </IconButton>
                    <FormControlLabel
                      label="Totals"
                      control={
                        <Checkbox
                          checked={!!totalsMap[fieldID]}
                          onChange={() => toggleTotal(fieldID, "Sum")}
                        />
                      }
                    />
                    <Box>
                      {(totalsMap[fieldID] || []).map(type => (
                        <Chip
                          key={type}
                          label={type}
                          onDelete={() => toggleTotal(fieldID, type)}
                          sx={{ ml: 0.5 }}
                        />
                      ))}
                      {summaryOptions.map(type => !totalsMap[fieldID]?.includes(type) && (
                        <Chip
                          key={type}
                          label={type}
                          variant="outlined"
                          onClick={() => toggleTotal(fieldID, type)}
                          sx={{ ml: 0.5, cursor: "pointer" }}
                        />
                      ))}
                    </Box>
                  </Box>
                );
              })}

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" gutterBottom>Available Fields</Typography>
              <Grid container spacing={1}>
                {availableFields.filter(f => !selectedFields.includes(f.fieldID)).map(f => (
                  <Grid item key={f.fieldID} xs={6} sm={4}>
                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={() => setSelectedFields([...selectedFields, f.fieldID])}
                    >
                      {f.fieldName}
                    </Button>
                  </Grid>
                ))}
              </Grid>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={resetDialog}>Cancel</Button>
          <Button onClick={handleDialogSave} variant="contained" disabled={!selectedTableID}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TableSelection;
